// src/components/MobileOrderPage.js
import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useReactToPrint } from 'react-to-print';
import './MobileOrderPage.css';

const url = process.env.REACT_APP_SERVER_URL || "https://food.stall-centralsoft.com/api";
const stallID = localStorage.getItem("stallID");
const username = localStorage.getItem("username");

export default function MobileOrderPage() {
  const [foods, setFoods] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cart, setCart] = useState([]);
  const [tableNumber, setTableNumber] = useState("");
  const [availableAddons, setAvailableAddons] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [loading, setLoading] = useState(true);
  const [showOrderConfirmation, setShowOrderConfirmation] = useState(false);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [itemQuantities, setItemQuantities] = useState({});
  const [addonQuantities, setAddonQuantities] = useState({});
  const [notes, setNotes] = useState({});

  const printRef = useRef();

  const PLACEHOLDER =
    "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='150' viewBox='0 0 200 150'%3E%3Crect width='200' height='150' fill='%23f8f9fa'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial' font-size='14' fill='%236c757d'%3ENo Image%3C/text%3E%3C/svg%3E";

  // Get image URL
  const getImageUrl = (imageURL) => {
    console.log('🖼️ Image Debug - Original URL:', imageURL);

    if (!imageURL || imageURL === "null" || imageURL === "undefined") {
      console.log('🖼️ Using placeholder - invalid URL');
      return PLACEHOLDER;
    }

    if (imageURL.startsWith('http') || imageURL.startsWith('data:')) {
      console.log('🖼️ Using direct URL:', imageURL);
      return imageURL;
    }

    const cleanImageURL = imageURL.replace(/^\//, '').trim();
    let filename;
    if (cleanImageURL.includes('/')) {
      filename = cleanImageURL.split('/').pop();
    } else {
      filename = cleanImageURL;
    }

    const finalUrl = `${url}/food-images/${filename}`;
    console.log('🖼️ Final URL:', finalUrl);
    return finalUrl;
  };

  // Safe numeric conversion
  const getSafePrice = (p) => {
    if (p === null || p === undefined || p === "") return 0;
    const n = Number(p);
    return Number.isFinite(n) ? n : 0;
  };

  // Safe toFixed function
  const safeToFixed = (value, decimals = 2) => {
    const num = getSafePrice(value);
    return num.toFixed(decimals);
  };

  // Print handler
  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    pageStyle: `
      @media print {
        body {
          font-family: 'Courier New', monospace;
          font-size: 12px;
          margin: 0;
          padding: 5px;
        }
        .no-print { display: none !important; }
        .print-only { display: block !important; }
        .kitchen-ticket {
          width: 80mm;
          border: 1px solid #000;
          padding: 8px;
          margin: 0 auto;
          background: white;
        }
        * {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
      @media screen {
        .print-only { display: none !important; }
      }
    `,
    documentTitle: `Order-${currentOrder?.orderId || ''}`
  });

  // Fetch foods and addons
  useEffect(() => {
    const fetchAll = async () => {
      try {
        setLoading(true);
        const [foodsRes, addonsRes] = await Promise.all([
          axios.get(`${url}/api/admin/foods`, { params: { stallID } }),
          axios.get(`${url}/api/admin/addons`, { params: { stallID } }),
        ]);

        // Normalize foods
        const normalizedFoods = (foodsRes.data || []).map((f) => {
          const id = f.id ?? f.ID ?? f._id ?? String(f.id);
          const foodName = f.foodName ?? f.name ?? f.food_name ?? "Unnamed";
          const rawPrice = getSafePrice(f.price);
          const rawProm = getSafePrice(f.prom_price ?? f.promPrice);
          const basePrice = rawProm > 0 ? rawProm : rawPrice;

          return {
            ...f,
            id: String(id),
            foodName,
            price: rawPrice,
            prom_price: rawProm,
            basePrice,
          };
        });

        setFoods(normalizedFoods);

        const uniqueCategories = [
          ...new Set(normalizedFoods.map((x) => x.category || "uncategorized")),
        ];
        setCategories(uniqueCategories);

        // Normalize addons
        const normalizedAddons = (addonsRes.data?.addons || addonsRes.data || []).map((a) => ({
          id: String(a.id ?? a._id ?? a.ID),
          name: a.name,
          price: getSafePrice(a.price),
          category: a.category || "extra",
        }));

        setAvailableAddons(normalizedAddons);
      } catch (err) {
        console.error("Fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  // Initialize quantities
  useEffect(() => {
    if (foods.length > 0 && availableAddons.length > 0) {
      const initialItemQuantities = {};
      const initialAddonQuantities = {};

      foods.forEach(item => {
        initialItemQuantities[item.id] = 1;
        initialAddonQuantities[item.id] = {};

        availableAddons.forEach(addon => {
          initialAddonQuantities[item.id][addon.id] = 0;
        });
      });

      setItemQuantities(initialItemQuantities);
      setAddonQuantities(initialAddonQuantities);
    }
  }, [foods, availableAddons]);

  // Handle quantity changes for main items
  const handleQuantityChange = (itemId, change) => {
    setItemQuantities(prev => {
      const currentQty = prev[itemId] || 1;
      const newQty = Math.max(1, currentQty + change);

      return {
        ...prev,
        [itemId]: newQty
      };
    });
  };

  // Handle quantity changes for addons
  const handleAddonQuantityChange = (itemId, addonId, change) => {
    setAddonQuantities(prev => {
      const currentItemAddons = prev[itemId] || {};
      const currentQty = currentItemAddons[addonId] || 0;
      const newQty = Math.max(0, currentQty + change);

      return {
        ...prev,
        [itemId]: {
          ...currentItemAddons,
          [addonId]: newQty
        }
      };
    });
  };

  // Handle note changes
  const handleNoteChange = (itemId, note) => {
    setNotes(prev => ({
      ...prev,
      [itemId]: note
    }));
  };

  // Add to cart function - UPDATED to handle same items with different addons
  const addToCart = (food) => {
    setCart((prev) => {
      const id = String(food.id);

      // Get current quantities and addons for this food item
      const quantity = itemQuantities[food.id] || 1;
      const itemAddons = addonQuantities[food.id] || {};
      const selectedAddons = [];
      let addonsTotal = 0;

      // Calculate addons and their total
      availableAddons.forEach(addon => {
        const addonQty = itemAddons[addon.id] || 0;
        if (addonQty > 0) {
          selectedAddons.push({
            ...addon,
            quantity: addonQty
          });
          addonsTotal += addon.price * addonQty;
        }
      });

      const note = notes[food.id] || '';
      const basePrice = food.prom_price > 0 ? food.prom_price : food.price;
      const itemTotal = (basePrice * quantity) + addonsTotal;

      // Create a unique identifier for this cart item configuration
      const addonsSignature = JSON.stringify(selectedAddons.sort((a, b) => a.id - b.id));
      const noteSignature = note;

      // Check if EXACT same item configuration exists (same addons + same notes)
      const existingItemIndex = prev.findIndex(cartItem =>
        String(cartItem.id) === id &&
        JSON.stringify(cartItem.selectedAddons.sort((a, b) => a.id - b.id)) === addonsSignature &&
        cartItem.notes === noteSignature
      );

      if (existingItemIndex !== -1) {
        // Update existing EXACT same item
        return prev.map((item, index) =>
          index === existingItemIndex
            ? {
                ...item,
                quantity: item.quantity + quantity,
                totalPrice: item.totalPrice + itemTotal
              }
            : item
        );
      } else {
        // Add as NEW item (different addons/notes)
        const cartItem = {
          id,
          foodName: food.foodName,
          quantity,
          selectedAddons,
          notes: note,
          basePrice,
          addonsTotal,
          totalPrice: itemTotal,
          // Add unique identifier for this configuration
          configId: `${id}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
        };
        return [...prev, cartItem];
      }
    });

    // Reset quantities and notes for this food item after adding to cart
    setItemQuantities(prev => ({
      ...prev,
      [food.id]: 1
    }));

    setAddonQuantities(prev => {
      const resetAddons = { ...prev[food.id] };
      Object.keys(resetAddons).forEach(addonId => {
        resetAddons[addonId] = 0;
      });

      return {
        ...prev,
        [food.id]: resetAddons
      };
    });

    setNotes(prev => ({
      ...prev,
      [food.id]: ''
    }));
  };

  // Update item quantity in cart - UPDATED to handle configId
  const updateItemQuantity = useCallback((itemId, change) => {
    setCart((prev) =>
      prev
        .map((item) =>
          (item.configId === itemId || String(item.id) === String(itemId))
            ? {
                ...item,
                quantity: Math.max(1, (item.quantity || 1) + change),
                totalPrice: (item.basePrice * Math.max(1, (item.quantity || 1) + change)) + item.addonsTotal
              }
            : item
        )
        .filter(item => item.quantity > 0)
    );
  }, []);

  // Remove item completely from cart - UPDATED to handle configId
  const removeItem = useCallback((itemId) => {
    setCart((prev) => prev.filter((i) =>
      !(i.configId === itemId || String(i.id) === String(itemId))
    ));
  }, []);

  // Handle addon dropdown selection - UPDATED to handle configId
  const handleAddonDropdown = useCallback((itemId, addonId) => {
    if (!addonId) return;

    setCart((prev) =>
      prev.map((item) => {
        if (!(item.configId === itemId || String(item.id) === String(itemId))) return item;

        const addon = availableAddons.find((a) => String(a.id) === String(addonId));
        if (!addon) return item;

        const existingIndex = (item.selectedAddons || []).findIndex(
          (a) => String(a.id) === String(addon.id)
        );

        let updatedAddons = [...(item.selectedAddons || [])];
        if (existingIndex >= 0) {
          updatedAddons[existingIndex] = {
            ...updatedAddons[existingIndex],
            quantity: (updatedAddons[existingIndex].quantity || 0) + 1,
          };
        } else {
          updatedAddons.push({ ...addon, quantity: 1 });
        }

        const addonsTotal = updatedAddons.reduce(
          (s, a) => s + getSafePrice(a.price) * (a.quantity || 0),
          0
        );

        const totalPrice = (item.basePrice * item.quantity) + addonsTotal;

        return {
          ...item,
          selectedAddons: updatedAddons,
          addonsTotal,
          totalPrice
        };
      })
    );
  }, [availableAddons]);

  // Update addon quantity in cart - UPDATED to handle configId
  const updateAddonQuantity = useCallback((itemId, addonId, change) => {
    setCart((prev) =>
      prev.map((item) => {
        if (!(item.configId === itemId || String(item.id) === String(itemId))) return item;

        let updatedAddons = (item.selectedAddons || []).map((a) =>
          String(a.id) === String(addonId)
            ? { ...a, quantity: Math.max(0, (a.quantity || 0) + change) }
            : a
        );

        // Remove zero quantity addons
        updatedAddons = updatedAddons.filter((a) => (a.quantity || 0) > 0);

        const addonsTotal = updatedAddons.reduce(
          (s, a) => s + getSafePrice(a.price) * (a.quantity || 0),
          0
        );

        const totalPrice = (item.basePrice * item.quantity) + addonsTotal;

        return {
          ...item,
          selectedAddons: updatedAddons,
          addonsTotal,
          totalPrice
        };
      })
    );
  }, []);

  // Update notes in cart - UPDATED to handle configId
  const updateNotes = useCallback((itemId, notes) => {
    setCart((prev) =>
      prev.map((item) =>
        (item.configId === itemId || String(item.id) === String(itemId)) ? { ...item, notes } : item
      )
    );
  }, []);

  // Calculate item total
  const calculateItemTotal = useCallback((item) => {
    return item.totalPrice || 0;
  }, []);

  // Cart total
  const cartTotal = cart.reduce((total, item) => total + (item.totalPrice || 0), 0);

  // Submit order
  const submitOrder = async () => {
    if (!tableNumber) {
      alert("Please enter table number");
      return;
    }
    if (cart.length === 0) {
      alert("Cart is empty");
      return;
    }

    try {
      const orderData = {
        stallID: stallID || "unknown",
        tableNumber,
        total: cartTotal,
        status: "pending",
      };

      const orderRes = await axios.post(`${url}/api/admin/orders`, orderData);

      if (orderRes.data.success) {
        const orderId = orderRes.data.orderId;

        // Prepare items for order confirmation
        const orderItems = [];

        for (const item of cart) {
          const addonsText =
            item.selectedAddons && item.selectedAddons.length > 0
              ? `Add-ons: ${item.selectedAddons
                  .map((addon) => `${addon.quantity} × ${addon.name}`)
                  .join(", ")}`
              : "";

          const notes = item.notes ? (addonsText ? `${item.notes} | ${addonsText}` : item.notes) : addonsText;

          const itemData = {
            stallID,
            orderId,
            foodId: item.id,
            foodName: item.foodName,
            quantity: item.quantity,
            unitPrice: getSafePrice(item.basePrice),
            notes,
            totalPrice: getSafePrice(item.totalPrice),
            selectedAddons: item.selectedAddons || [],
            basePrice: item.basePrice,
            addonsTotal: item.addonsTotal
          };

          await axios.post(`${url}/api/admin/order-items`, itemData);
          orderItems.push(itemData);
        }

        // Set current order for confirmation and printing
        const currentOrderData = {
          orderId,
          stallID,
          tableNumber,
          items: orderItems,
          totalAmount: cartTotal,
          orderTime: new Date().toLocaleString('en-MY')
        };

        setCurrentOrder(currentOrderData);
        setShowOrderConfirmation(true);

        // Clear cart after successful order
        setCart([]);
        setTableNumber("");

      } else {
        alert("Failed to create order");
      }
    } catch (err) {
      console.error("Order error:", err);
      alert("Error placing order: " + (err?.response?.data?.error || err.message));
    }
  };

  // Back to menu function
  const handleBackToMenu = () => {
    setShowOrderConfirmation(false);
    setCurrentOrder(null);
  };

  const filteredFoods =
    selectedCategory === "All"
      ? foods
      : foods.filter(
          (f) => (f.category || "uncategorized") === selectedCategory
        );

  // Order Confirmation Component
  const OrderConfirmation = ({ order, onBack, onPrint, printRef }) => {
    return (
      <div className="container-fluid">
        <div className="no-print mb-4">
          <button
            className="btn btn-secondary me-2"
            onClick={onBack}
          >
            ← Back to Menu
          </button>
          <button
            className="btn btn-primary"
            onClick={onPrint}
          >
            🖨️ Print Order Receipt
          </button>
        </div>

        {/* Printable Receipt */}
        <div ref={printRef} className="kitchen-ticket">
          <div style={{ textAlign: "center", marginBottom: "10px", borderBottom: "1px solid #000", paddingBottom: "8px" }}>
            <h2 style={{ margin: "0", fontSize: "16px", fontWeight: "bold" }}>{order.stallID.toUpperCase()} KITCHEN</h2>
            <p style={{ margin: "2px 0", fontSize: "10px" }}>ORDER RECEIPT</p>
          </div>

          <div style={{ marginBottom: "8px", borderBottom: "1px dashed #000", paddingBottom: "8px" }}>
            <p style={{ margin: "2px 0", fontSize: "10px" }}><strong>Order #:</strong> {order.orderId}</p>
            <p style={{ margin: "2px 0", fontSize: "10px" }}><strong>Table #:</strong> {order.tableNumber}</p>
            <p style={{ margin: "2px 0", fontSize: "10px" }}><strong>Time:</strong> {order.orderTime}</p>
          </div>

          <div style={{ marginBottom: "8px" }}>
            {order.items.map((item, index) => (
              <div key={index} style={{ marginBottom: "10px", borderBottom: "1px dotted #000", paddingBottom: "5px" }}>
                <p style={{ margin: "2px 0", fontSize: "11px", fontWeight: "bold" }}>
                  {item.quantity} x {item.foodName}
                </p>
                <p style={{ margin: "2px 0", fontSize: "10px" }}>
                  RM {safeToFixed(item.unitPrice)} × {item.quantity} = RM {safeToFixed(item.totalPrice)}
                </p>

                {item.notes && (
                  <p style={{ margin: "2px 0", fontSize: "9px", fontStyle: "italic" }}>
                    ** {item.notes}
                  </p>
                )}

                {item.selectedAddons && item.selectedAddons.length > 0 && (
                  <div style={{ marginLeft: "8px" }}>
                    {item.selectedAddons.map((addon, addonIndex) => (
                      <p key={addonIndex} style={{ margin: "1px 0", fontSize: "9px" }}>
                        + {addon.quantity} x {addon.name} (+RM {safeToFixed(addon.price)})
                      </p>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          <div style={{ borderTop: "2px solid #000", paddingTop: "8px", textAlign: "center" }}>
            <p style={{ margin: "2px 0", fontSize: "11px", fontWeight: "bold" }}>
              TOTAL: RM {safeToFixed(order.totalAmount)}
            </p>
          </div>

          <div style={{ textAlign: "center", marginTop: "10px", fontSize: "8px" }}>
            <p>*** KITCHEN COPY ***</p>
            <p>Thank you for your order!</p>
          </div>
        </div>

        {/* Screen Confirmation */}
        <div className="no-print mt-4">
          <div className="alert alert-success">
            <h4 className="alert-heading">✅ Order Placed Successfully!</h4>
            <div className="row">
              <div className="col-md-6">
                <p><strong>Order ID:</strong> {order.orderId}</p>
                <p><strong>Table Number:</strong> {order.tableNumber}</p>
              </div>
              <div className="col-md-6">
                <p><strong>Total Amount:</strong> RM {safeToFixed(order.totalAmount)}</p>
                <p><strong>Order Time:</strong> {order.orderTime}</p>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Order Summary</h5>
            </div>
            <div className="card-body">
              {order.items.map((item, index) => (
                <div key={index} className="mb-3 p-3 border rounded">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <h6 className="mb-1">{item.quantity}x {item.foodName}</h6>
                      <p className="mb-1 text-success">
                        RM {safeToFixed(item.unitPrice)} × {item.quantity} = RM {safeToFixed(item.totalPrice)}
                      </p>
                      {item.selectedAddons && item.selectedAddons.length > 0 && (
                        <div className="mt-2">
                          <small className="text-muted">Add-ons:</small>
                          {item.selectedAddons.map((addon, addonIndex) => (
                            <div key={addonIndex} className="ms-2">
                              <small>+ {addon.quantity}x {addon.name} (+RM {safeToFixed(addon.price * addon.quantity)})</small>
                            </div>
                          ))}
                        </div>
                      )}
                      {item.notes && (
                        <div className="mt-1">
                          <small className="text-muted">Note: {item.notes}</small>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="mobile-order-page">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <span>Loading...</span>
        </div>
      </div>
    );
  }

  if (showOrderConfirmation && currentOrder) {
    return (
      <OrderConfirmation
        order={currentOrder}
        onBack={handleBackToMenu}
        onPrint={handlePrint}
        printRef={printRef}
      />
    );
  }

  return (
    <div className="mobile-order-page">
      {/* Header */}
      <header className="mobile-header">
        <h1>🍽️ Food Ordering 點餐</h1>
        <div className="stall-info">
          Stall: <strong>{username}</strong>
        </div>
        <div className="table-number-section">
          <div className="table-input-group">
            <label className="table-label">📋 TABLE NUMBER 餐桌號 *</label>
            <input
              type="text"
              className="table-input"
              value={tableNumber}
              onChange={(e) => setTableNumber(e.target.value)}
              placeholder="Enter table number (e.g., T01, T02)"
              required
            />
            {!tableNumber && (
              <div className="table-warning">Please enter your table number 請輸入餐桌號</div>
            )}
          </div>
        </div>
      </header>

      {/* Category Filter */}
      <div className="category-filter">
        <button
          className={`category-btn ${selectedCategory === "All" ? "active" : ""}`}
          onClick={() => setSelectedCategory("All")}
        >
          All
        </button>
        {categories.map((c) => (
          <button
            key={c}
            className={`category-btn ${selectedCategory === c ? "active" : ""}`}
            onClick={() => setSelectedCategory(c)}
          >
            {c}
          </button>
        ))}
      </div>

      {/* Menu Items in 2 Columns */}
      <div className="menu-grid">
        {filteredFoods.map((food) => (
          <div key={String(food.id)} className="food-card">
            <div style={{
              height: 180,
              backgroundColor: "#f8f9fa",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              overflow: 'hidden'
            }}>
              <img
                src={getImageUrl(food.imageURL)}
                alt={food.foodName}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover"
                }}
                className="img-thumbnail"
                crossOrigin="anonymous"
                onError={(e) => {
                  console.log('🖼️ Image error for:', food.foodName, 'URL:', food.imageURL);
                  e.target.src = PLACEHOLDER;
                }}
              />
            </div>
            <div className="food-content">
              <h3 className="food-name">{food.foodName}</h3>
              <p className="food-description">{food.description}</p>

              <div className="price-section">
                {food.prom_price > 0 && food.prom_price < food.price ? (
                  <>
                    <span className="original-price">RM {safeToFixed(food.price)}</span>
                    <span className="promo-price">RM {safeToFixed(food.prom_price)}</span>
                  </>
                ) : (
                  <span className="normal-price">RM {safeToFixed(food.price)}</span>
                )}
              </div>

              <button
                className="add-to-cart-btn"
                onClick={() => addToCart(food)}
              >
                Order Now 下單
              </button>
            </div>
          </div>
        ))}
        {foods.length === 0 && <p className="no-food-message">No foods found.</p>}
      </div>

      {/* Fixed Cart at Bottom */}
       {/* Fixed Cart at Bottom */}
      <div className="mobile-cart">
        <div className="cart-header">
          <div className="cart-title">
            🛒 Your Order 您的訂購食物

       <span className="item-count">({cart.reduce((total, item) => total + item.quantity, 0)} items)</span>
          </div>
          <div className="cart-total-amount">RM {safeToFixed(cartTotal)}</div>
        </div>

        <div className="cart-items">
          {cart.length === 0 ? (
            <div className="empty-cart">Cart is empty</div>
          ) : (
            cart.map((item, index) => (
              <div key={item.configId || `${item.id}-${index}`} className="cart-item">
                <div className="cart-item-header">
                  <span className="item-name">
                    {item.foodName}
                    {(item.selectedAddons || item.selectedAddons.length > 0 || item.notes) && (
                      <span className="customized-badge"> (Customized)</span>
                    )}
                  </span>
                  <div className="item-quantity-controls">
                    <button
                      className="qty-btn"
                      onClick={() => updateItemQuantity(item.configId || item.id, -1)}
                    >
                      -
                    </button>
                    <span className="item-quantity">{item.quantity}</span>
                    <button
                      className="qty-btn"
                      onClick={() => updateItemQuantity(item.configId || item.id, +1)}
                    >
                      +
                    </button>
                  </div>
                </div>

                {/* Show base price */}
                <div className="item-price-display">
                  <small>Base: RM {safeToFixed(item.basePrice)} × {item.quantity} = RM {safeToFixed(item.basePrice * item.quantity)}</small>
                </div>

         {/* Add-ons Dropdown - Always show to allow adding multiple addons */}
<div className="addon-dropdown">
  <select
    className="addon-select"
    defaultValue=""
    onChange={(e) => {
      const val = e.target.value;
      if (val) handleAddonDropdown(item.configId || item.id, val);
      e.target.value = "";
    }}
  >
    <option value="">+ Add an add-on... 添加食料</option>
    {availableAddons.map((a) => (
      <option key={a.id} value={a.id}>
        {a.name} (+RM {safeToFixed(a.price)})
      </option>
    ))}
  </select>
</div>

                )}

                {/* Selected Add-ons */}
                {item.selectedAddons && item.selectedAddons.length > 0 && (
                  <div className="selected-addons">
                    <div className="addons-title">Customizations:</div>
                    {item.selectedAddons.map((a) => (
                      <div key={String(a.id)} className="addon-item">
                        <span className="addon-name">{a.name}</span>
                        <div className="addon-controls">
                          <button
                            className="addon-qty-btn"
                            onClick={() => updateAddonQuantity(item.configId || item.id, a.id, -1)}
                          >
                            -
                          </button>
                          <span className="addon-quantity">{a.quantity}</span>
                          <button
                            className="addon-qty-btn"
                            onClick={() => updateAddonQuantity(item.configId || item.id, a.id, +1)}
                          >
                            +
                          </button>
                        </div>
                        <span className="addon-price">+RM {safeToFixed(a.price * a.quantity)}</span>
                      </div>
                    ))}
                    {item.addonsTotal > 0 && (
                      <div className="addons-total">
                        Add-ons total: +RM {safeToFixed(item.addonsTotal)}
                      </div>
                    )}
                  </div>
                )}

                {/* Notes Input */}
                <div className="notes-section">
                  <input
                    type="text"
                    className="notes-input"
                    placeholder="Special instructions (no chili, less sugar, etc.) 特別指示"
                    value={item.notes || ""}
                    onChange={(e) => updateNotes(item.configId || item.id, e.target.value)}
                  />
                </div>

                {/* Item Total */}
                <div className="item-total">
                  <span className="total-amount">Total: RM {safeToFixed(item.totalPrice)}</span>
                  <button
                    className="remove-btn"
                    onClick={() => removeItem(item.configId || item.id)}
                  >
                    🗑
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Place Order Button - This was missing due to syntax errors */}
        <button
          className="place-order-btn"
          onClick={submitOrder}
          disabled={!tableNumber || cart.length === 0}
        >
          📋 Place Order Total 訂單總計 - RM {safeToFixed(cartTotal)}
        </button>
      </div>
    </div>
  );
}
